import { useState } from 'react';
import { useForm } from 'react-hook-form';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import dayjs from 'dayjs';
import { isAxiosError } from 'axios';
import { toast } from 'App';
import { publicNotificationsService } from 'views/services/publicNotificationsService';
import { EPublicNotificationType } from 'views/types/publicNotifications';

interface IFormValues {
  type: EPublicNotificationType;
  subject: string;
  body: string;
}

const TYPE_LABELS: Record<EPublicNotificationType, string> = {
  [EPublicNotificationType.UPDATE]: '🔄 Update',
  [EPublicNotificationType.ANNOUNCEMENT]: '📢 Announcement',
  [EPublicNotificationType.FEATURE]: '✨ New Feature',
};

const inputCls =
  'w-full rounded-lg border border-gray-300 p-2.5 text-navy-700 outline-none focus:border-brand-500 dark:border-white/10 dark:bg-navy-900 dark:text-white';
const labelCls = 'mb-1 block text-sm font-medium text-navy-700 dark:text-white';
const hintCls = 'mt-1 text-xs text-gray-400';

interface IProps {
  onCreated(): void;
}

export const ComposeForm: React.FC<IProps> = ({ onCreated }) => {
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState<'now' | 'schedule'>('now');
  const [scheduledDate, setScheduledDate] = useState<Date | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<IFormValues>({
    mode: 'onChange',
    defaultValues: { type: EPublicNotificationType.ANNOUNCEMENT },
  });

  const subjectValue = watch('subject') ?? '';
  const bodyValue = watch('body') ?? '';

  const onSubmit = async (values: IFormValues) => {
    if (mode === 'schedule' && !scheduledDate) {
      toast({
        title: 'Pick a date and time for the scheduled notification',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    try {
      setSaving(true);
      await publicNotificationsService.create({
        type: values.type,
        subject: values.subject.trim(),
        body: values.body.trim(),
        scheduledAt:
          mode === 'schedule' && scheduledDate
            ? dayjs(scheduledDate).toISOString()
            : undefined,
      });
      toast({
        title:
          mode === 'schedule' && scheduledDate
            ? `Notification scheduled for ${dayjs(scheduledDate).format('MMM D, YYYY HH:mm')}`
            : 'Broadcast queued — sending starts within 30 seconds',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      reset({ type: values.type, subject: '', body: '' });
      setScheduledDate(null);
      setMode('now');
      onCreated();
    } catch (error) {
      const message = isAxiosError(error)
        ? (error.response?.data?.message ?? error.message)
        : 'Failed to create notification';
      toast({
        title: 'Create failed',
        description: String(message),
        status: 'error',
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-2xl bg-white p-6 shadow-sm dark:bg-navy-800">
      <h2 className="mb-1 text-xl font-bold text-navy-700 dark:text-white">
        New Notification
      </h2>
      <p className="mb-6 text-sm text-gray-500 dark:text-gray-400">
        Sent to every active bot user as a Telegram message.
      </p>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        <div>
          <label className={labelCls}>Type</label>
          <select className={inputCls} {...register('type')}>
            {Object.values(EPublicNotificationType).map(type => (
              <option key={type} value={type}>
                {TYPE_LABELS[type]}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className={labelCls}>Subject</label>
          <input
            type="text"
            maxLength={200}
            className={inputCls}
            placeholder="Short headline"
            {...register('subject', {
              required: 'Required',
              maxLength: { value: 200, message: 'Max 200 characters' },
              validate: value => value.trim().length > 0 || 'Required',
            })}
          />
          {errors.subject && (
            <p className="mt-1 text-xs text-red-500">
              {errors.subject.message}
            </p>
          )}
          <p className={hintCls}>{subjectValue.length}/200</p>
        </div>

        <div>
          <label className={labelCls}>Body</label>
          <textarea
            rows={6}
            maxLength={3500}
            className={inputCls}
            placeholder="Message text users will receive"
            {...register('body', {
              required: 'Required',
              maxLength: { value: 3500, message: 'Max 3500 characters' },
              validate: value => value.trim().length > 0 || 'Required',
            })}
          />
          {errors.body && (
            <p className="mt-1 text-xs text-red-500">{errors.body.message}</p>
          )}
          <p className={hintCls}>{bodyValue.length}/3500</p>
        </div>

        <div>
          <label className={labelCls}>Posting</label>
          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2 text-sm text-navy-700 dark:text-white">
              <input
                type="radio"
                checked={mode === 'now'}
                onChange={() => setMode('now')}
              />
              Send now
            </label>
            <label className="flex items-center gap-2 text-sm text-navy-700 dark:text-white">
              <input
                type="radio"
                checked={mode === 'schedule'}
                onChange={() => setMode('schedule')}
              />
              Schedule
            </label>
          </div>
          {mode === 'schedule' && (
            <div className="mt-3">
              <DatePicker
                selected={scheduledDate}
                onChange={(date: Date | null) => setScheduledDate(date)}
                showTimeSelect
                timeIntervals={5}
                minDate={new Date()}
                dateFormat="MMM d, yyyy HH:mm"
                placeholderText="Pick date and time"
                className={inputCls}
                popperClassName="react-datepicker-popper"
              />
              <p className={hintCls}>
                Uses your local timezone. Sending starts within 30 seconds of
                the chosen time.
              </p>
            </div>
          )}
        </div>

        <button
          type="submit"
          disabled={saving}
          className="rounded-lg bg-brand-500 px-5 py-2.5 font-medium text-white transition-colors hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {saving
            ? 'Saving…'
            : mode === 'schedule'
              ? 'Schedule notification'
              : 'Send to all users'}
        </button>
      </form>
    </div>
  );
};
